'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils/helpers';
import routes from '@/lib/config/routes';
import { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, ChevronDown, Home, School, Briefcase, HeartHandshake, BookOpen, Users2 } from 'lucide-react';
import { MainLogoText } from '@/components/MainLogo';

const useCaseItems = [
  {
    title: 'Co-Living & Roommates',
    description: 'Match compatible roommates',
    href: '/usecases/co-living-roommate-matching',
    icon: Home,
  },
  {
    title: 'Educational Institutions',
    description: 'Student groups & teams',
    href: '/usecases/educational-institutions',
    icon: School,
  },
  {
    title: 'Corporate Teams',
    description: 'High-performing teams',
    href: '/usecases/corporate-team-formation',
    icon: Briefcase,
  },
  {
    title: 'Mentorship Programs',
    description: 'Mentor-mentee matching',
    href: '/usecases/mentorship-programs',
    icon: HeartHandshake,
  },
  {
    title: 'Training & Development',
    description: 'Learning cohorts',
    href: '/usecases/training-development',
    icon: BookOpen,
  },
  {
    title: 'Community Organizations',
    description: 'Volunteer coordination',
    href: '/usecases/community-organizations',
    icon: Users2,
  },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUseCaseOpen, setIsUseCaseOpen] = useState(false);
  const [isMobileUseCaseOpen, setIsMobileUseCaseOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUseCaseOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsUseCaseOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsUseCaseOpen(false);
    }, 150);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-slate-950/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/10'
          : 'bg-transparent'
      )}
    >
      <div className="container mx-auto">
        <div className="flex h-16 lg:h-20 items-center justify-between px-4 lg:px-8">
          {/* Logo - Links to home */}
          <div className="flex-shrink-0">
            <MainLogoText href="/" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {routes.routes_marketing.map((item) => {
              // Special handling for Use Cases with dropdown
              if (item.link === '/usecases') {
                return (
                  <div
                    key={item.link}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={cn(
                        'flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg',
                        isUseCaseOpen
                          ? 'text-white bg-white/5'
                          : 'text-slate-300 hover:text-white hover:bg-white/5'
                      )}
                      onClick={() => setIsUseCaseOpen(!isUseCaseOpen)}
                    >
                      {item.title}
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform duration-200',
                          isUseCaseOpen && 'rotate-180'
                        )}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={cn(
                        'absolute top-full left-0 mt-2 w-80 py-3 rounded-xl transition-all duration-200',
                        'bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50',
                        isUseCaseOpen
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 -translate-y-2 pointer-events-none'
                      )}
                    >
                      {/* View All Link */}
                      <Link
                        href="/usecases"
                        className="flex items-center justify-between px-4 py-2 mx-2 mb-2 text-sm font-medium text-amber-500 hover:bg-white/5 rounded-lg transition-colors"
                      >
                        View All Use Cases
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <div className="h-px bg-white/10 mx-4 mb-2" />

                      {/* Use Case Items */}
                      {useCaseItems.map((useCase) => (
                        <Link
                          key={useCase.href}
                          href={useCase.href}
                          className="flex items-start gap-3 px-4 py-3 mx-2 hover:bg-white/5 rounded-lg transition-colors group"
                          onClick={() => setIsUseCaseOpen(false)}
                        >
                          <div className="p-2 rounded-lg bg-amber-600/10 group-hover:bg-amber-600/20 transition-colors">
                            <useCase.icon className="w-4 h-4 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white group-hover:text-amber-500 transition-colors">
                              {useCase.title}
                            </p>
                            <p className="text-xs text-slate-400">{useCase.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.link}
                  href={item.link}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                >
                  {item.title}
                </Link>
              );
            })}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/auth/login"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Sign In
            </Link>
            <Link
              href="/auth/signup"
              className={cn(
                'group inline-flex items-center gap-2 px-5 py-2.5 rounded-full',
                'bg-gradient-to-r from-amber-600 to-amber-700',
                'hover:from-amber-700 hover:to-amber-800',
                'text-white text-sm font-semibold',
                'shadow-lg shadow-amber-900/30 hover:shadow-amber-900/50',
                'transition-all duration-300'
              )}
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={cn(
            'lg:hidden absolute left-0 right-0 top-full transition-all duration-300 overflow-hidden',
            isMenuOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0'
          )}
        >
          <nav className="flex flex-col py-4 px-4 space-y-1 bg-slate-900/95 backdrop-blur-xl border-t border-white/10">
            {routes.routes_marketing.map((item) => {
              // Special handling for Use Cases with accordion
              if (item.link === '/usecases') {
                return (
                  <div key={item.link}>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      onClick={() => setIsMobileUseCaseOpen(!isMobileUseCaseOpen)}
                    >
                      {item.title}
                      <ChevronDown
                        className={cn(
                          'w-5 h-5 transition-transform duration-200',
                          isMobileUseCaseOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-300',
                        isMobileUseCaseOpen ? 'max-h-screen' : 'max-h-0'
                      )}
                    >
                      <div className="pl-4 py-2 space-y-1">
                        <Link
                          href="/usecases"
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-500 hover:bg-white/5 rounded-lg transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          View All Use Cases
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                        {useCaseItems.map((useCase) => (
                          <Link
                            key={useCase.href}
                            href={useCase.href}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <useCase.icon className="w-4 h-4 text-amber-500/70" />
                            {useCase.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.link}
                  href={item.link}
                  className="px-4 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.title}
                </Link>
              );
            })}
            <div className="h-px bg-white/10 my-2" />
            <Link
              href="/auth/login"
              className="px-4 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link
              href="/auth/signup"
              className="mx-2 mt-2 py-3 rounded-full bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold text-center"
              onClick={() => setIsMenuOpen(false)}
            >
              Get Started Free
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};
